import {ref} from 'vue';
import {FetchError} from '@navbar-api';
import {useUserStore} from '@navbar-logged/features/user';
import {UmbrellaCommercialContract} from '@navbar-logged/registry/apis.registry';
import {defineStore} from 'pinia';
import {useDataTracking, useTranslation} from '#imports';
import {readonlyState, useErrorHandler, withCleanup} from '@malt/utils';

export const useLegalAgreementAcceptanceStore = defineStore('navbar-legal-agreement-acceptance', () => {
    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {handleUnknownError} = useErrorHandler();
    const userStore = useUserStore();
    const acceptedVersionId = ref<string>();
    const isAccepting = ref(false);
    const hasVersionConflict = ref(false);

    async function acceptAgreement(companyId: string, versionId: string) {
        if (isAccepting.value || acceptedVersionId.value === versionId) {
            return;
        }
        isAccepting.value = true;
        try {
            await UmbrellaCommercialContract.acceptUmbrellaCommercialContract({
                companyId,
                acceptUmbrellaCommercialContractRequest: {versionId},
            });
            acceptedVersionId.value = versionId;
            void track('umbrella_company_commercial_agreement_submitted', {
                company_id: companyId,
                agreement_version_id: versionId,
            });
        } catch (error) {
            if (error instanceof FetchError && error.statusCode === 409) {
                hasVersionConflict.value = true;
                try {
                    await userStore.refreshUser();
                } catch (refreshError) {
                    handleUnknownError(refreshError, {
                        customErrorMessage: t('compliance.legal.agreement.acceptance.umbrella.error'),
                        showSnackbar: true,
                    });
                }
                return;
            }
            handleUnknownError(error, {customErrorMessage: t('compliance.legal.agreement.acceptance.umbrella.error'), showSnackbar: true});
        } finally {
            isAccepting.value = false;
        }
    }

    function forgetAcceptance() {
        acceptedVersionId.value = undefined;
        hasVersionConflict.value = false;
    }

    return {
        ...withCleanup({
            acceptedVersionId: readonlyState(acceptedVersionId),
            hasVersionConflict: readonlyState(hasVersionConflict),
            isAccepting: readonlyState(isAccepting),
        }),

        acceptAgreement,
        forgetAcceptance,
    };
});
